import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import { LAPTOP_SCREEN } from 'consts';
import { useMatchMedia } from 'hooks/useMatchMedia';
import { getSeoAltTags } from 'utils/formatStrings';

import { HorizontalSlider } from './HorizontalSlider';
import MainImage from './MainImage';
import ThumbImage from './ThumbImage';

import { Slider } from 'ui';

import style from './gallery.module.scss';

const SLIDES_TO_SHOW = 3;

export const Gallery = ({ product }) => {
  const isLaptop = useMatchMedia(`(max-width: ${LAPTOP_SCREEN}px)`);

  const [isSynchWithActualSlider, setIsSynchWithActualSlider] = useState(true);
  const [currImg, setCurrImg] = useState({
    id: null,
    src: null,
    webpSrc: null,
    alt_tag: null,
  });
  const desktopSliderRef = useRef(null);
  const mainSliderRef = useRef(null);

  const [actualSlider, setIsActualSlider] = useState(
    !isLaptop ? desktopSliderRef : undefined,
  );

  useEffect(
    () =>
      setIsActualSlider(
        isSynchWithActualSlider && !isLaptop ? desktopSliderRef : undefined,
      ),

    [isLaptop, isSynchWithActualSlider],
  );

  const imagesForRender = useMemo(() => {
    if (
      !product ||
      !product.images ||
      !Array.isArray(product.images) ||
      !product.images.length > 1
    ) {
      return null;
    }

    return [...product.images];
  }, [product]);

  const { mainImageIndex, mainImage } = useMemo(() => {
    let image = null;
    let index = 0;

    if (product.image) {
      image = product.image;
      index = imagesForRender?.findIndex((el) => el.id === image.id);
    } else if (product.images && product.images.length) {
      image = product.images[0];
    }
    return { mainImageIndex: index, mainImage: image };
  }, [product, imagesForRender]);

  const initialSlide = useMemo(
    () => (currImg.index >= 0 ? currImg.index : mainImageIndex),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mainImageIndex, isLaptop],
  );

  const toggleActiveClass = useCallback((e) => {
    e.currentTarget.classList.toggle(style.orangeArrow);
  }, []);

  const consistentAltTag = useMemo(() => {
    return getSeoAltTags({ product });
  }, [product]);

  const handleSetCurrentImage = useCallback(
    (el, index, isUpdateMainSlider) => {
      if (!el) {
        setCurrImg({
          id: null,
          src: null,
          webpSrc: null,
          alt_tag: consistentAltTag,
        });
        return;
      }

      setCurrImg({
        id: el.id,
        src: el.large || el.src,
        webpSrc: el.webp?.large,
        alt_tag: el.alt_tag || consistentAltTag,
        index,
      });

      if (isUpdateMainSlider && mainSliderRef.current) {
        mainSliderRef.current.slickGoTo(index);
      }
    },
    [consistentAltTag],
  );

  useEffect(() => {
    handleSetCurrentImage(mainImage, mainImageIndex);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainImageIndex]);

  const handleSwipeMainSlider = useCallback(
    (index) => {
      if (!isSynchWithActualSlider) {
        setIsSynchWithActualSlider(true);
      }
      if (imagesForRender?.length <= SLIDES_TO_SHOW) {
        handleSetCurrentImage(imagesForRender[index], index);
      }
    },
    [isSynchWithActualSlider, imagesForRender, handleSetCurrentImage],
  );

  const handleClickImage = (el, index, update) => {
    setIsSynchWithActualSlider(false);
    handleSetCurrentImage(el, index, update);
  };

  return (
    <div className={style.galleryWrapper}>
      {imagesForRender?.length ? (
        <div className={style.galleryAll}>
          <div className={clsx(style.galleryMain, style.galleryExistedImages)}>
            <div className={style.mainImageContainer}>
              <Slider
                ref={mainSliderRef}
                asNavFor={actualSlider?.current}
                dotsWrapperClassName={style.sliderDotsWrapper}
                dotsClassName={style.sliderDots}
                slidesToShow={1}
                arrows={false}
                initialSlide={initialSlide}
                className={style.mainImageWrapper}
                afterChange={(index) => handleSwipeMainSlider(index)}
                dots={isLaptop}
              >
                {imagesForRender.map((el, i) => (
                  <MainImage
                    key={i}
                    item={el}
                    consistentAltTag={consistentAltTag}
                    productName={product.name}
                    className={style.mainImage}
                  />
                ))}
              </Slider>
            </div>
          </div>
          {!isLaptop && (
            <div className={style.gallery}>
              <HorizontalSlider
                ref={desktopSliderRef}
                asNavFor={mainSliderRef.current}
                slidesToShow={SLIDES_TO_SHOW}
                centerMode
                initialSlide={initialSlide}
                toggleActiveClass={toggleActiveClass}
                beforeChange={(_, index) =>
                  handleSetCurrentImage(imagesForRender[index], index)
                }
              >
                {imagesForRender.map((el, i) => (
                  <ThumbImage
                    currImgId={currImg.id}
                    el={el}
                    key={i}
                    index={i}
                    handleSetCurrentImage={handleClickImage}
                    altTag={consistentAltTag}
                  />
                ))}
              </HorizontalSlider>
            </div>
          )}
        </div>
      ) : (
        <div className={style.galleryMain}>
          <div className={style.mainImageContainer}>
            <MainImage
              item={currImg}
              consistentAltTag={consistentAltTag}
              productName={product.name}
              className={style.singleImage}
            />
          </div>
        </div>
      )}
    </div>
  );
};
